import { Component, ElementRef, HostListener, inject, output, signal, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { AuthService } from '@core/services/auth.service';
import {
  PASSWORD_MAX,
  STRENGTH_LABEL,
  evaluatePassword,
  passwordIsValid,
  passwordStrength
} from '@core/models/password-policy';

/**
 * Diálogo de cambio de contraseña.
 *
 * Muestra las reglas de seguridad como una lista que se va marcando mientras
 * se escribe, en vez de rechazar al pulsar Guardar: pedir una contraseña
 * "segura" sin decir qué se espera es lo que lleva a intentarlo cinco veces y
 * acabar poniendo la variante más pobre que el sistema acepte.
 *
 * El diálogo NO se cierra al fallar: un error del servidor —la contraseña
 * actual equivocada, por ejemplo— obligaría a rellenar los tres campos otra
 * vez.
 */
@Component({
  selector: 'erp-change-password-dialog',
  standalone: true,
  imports: [FormsModule],
  template: `
<div class="fixed inset-0 z-[1400] flex items-center justify-center bg-slate-950/60 p-4"
     (mousedown)="armBackdrop($event)" (click)="closeOnBackdrop($event)">
  <div class="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl dark:bg-gray-800">

    <div class="flex items-start justify-between gap-3">
      <div>
        <h2 class="text-lg font-bold text-gray-900 dark:text-white">Cambiar contraseña</h2>
        <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Al guardar se cerrarán tus sesiones en otros dispositivos.
        </p>
      </div>
      <button type="button" (click)="closed.emit()" aria-label="Cerrar"
              class="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700">
        <svg fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="h-5 w-5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
        </svg>
      </button>
    </div>

    @if (done()) {
      <div class="mt-5 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700
                  dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">
        Contraseña actualizada. Úsala la próxima vez que inicies sesión.
      </div>
      <div class="mt-5 flex justify-end">
        <button type="button" (click)="closed.emit()"
                class="rounded-lg bg-cyan-600 px-4 py-2 text-sm font-semibold text-white hover:bg-cyan-700">
          Entendido
        </button>
      </div>
    } @else {
      <div class="mt-5 space-y-3">
        <div>
          <label class="mb-1 block text-xs font-semibold text-gray-600 dark:text-gray-300">
            Contraseña actual
          </label>
          <input #actualInput [type]="verActual() ? 'text' : 'password'" [(ngModel)]="actual"
                 autocomplete="current-password" [class]="inputClass"/>
          <button type="button" (click)="verActual.set(!verActual())"
                  class="mt-1 text-[11px] font-medium text-cyan-700 hover:underline dark:text-cyan-400">
            {{ verActual() ? 'Ocultar' : 'Mostrar' }}
          </button>
        </div>

        <div>
          <label class="mb-1 block text-xs font-semibold text-gray-600 dark:text-gray-300">
            Contraseña nueva
          </label>
          <input [type]="verNueva() ? 'text' : 'password'" [ngModel]="nueva()"
                 (ngModelChange)="nueva.set($event)" [maxlength]="max"
                 autocomplete="new-password" [class]="inputClass"/>
          <button type="button" (click)="verNueva.set(!verNueva())"
                  class="mt-1 text-[11px] font-medium text-cyan-700 hover:underline dark:text-cyan-400">
            {{ verNueva() ? 'Ocultar' : 'Mostrar' }}
          </button>

          @if (nueva()) {
            <div class="mt-2 flex items-center gap-2">
              <div class="h-1.5 flex-1 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700">
                <div class="h-full rounded-full transition-all"
                     [class]="barColor()" [style.width.%]="(strength() + 1) * 20"></div>
              </div>
              <span class="w-20 shrink-0 text-right text-[11px] font-semibold" [class]="textColor()">
                {{ strengthLabel() }}
              </span>
            </div>
          }
        </div>

        <div>
          <label class="mb-1 block text-xs font-semibold text-gray-600 dark:text-gray-300">
            Repite la contraseña nueva
          </label>
          <input type="password" [ngModel]="repetida()" (ngModelChange)="repetida.set($event)"
                 [maxlength]="max" autocomplete="new-password" [class]="inputClass"/>
          @if (repetida() && !coinciden()) {
            <p class="mt-1 text-xs text-red-600 dark:text-red-400">Las dos contraseñas no coinciden.</p>
          }
        </div>
      </div>

      <!-- Requisitos: se marcan en verde conforme se cumplen -->
      <ul class="mt-4 space-y-1 rounded-xl bg-gray-50 p-3 dark:bg-gray-900/50">
        @for (regla of reglas(); track regla.id) {
          <li class="flex items-start gap-2 text-xs"
              [class]="regla.ok ? 'text-emerald-700 dark:text-emerald-400' : 'text-gray-500 dark:text-gray-400'">
            <span class="mt-px shrink-0 font-bold">{{ regla.ok ? '✓' : '·' }}</span>
            <span>{{ regla.label }}</span>
          </li>
        }
      </ul>

      @if (error(); as e) {
        <div class="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700
                    dark:border-red-800 dark:bg-red-950 dark:text-red-300">
          <p>{{ e }}</p>
          @if (detalles().length) {
            <ul class="mt-2 list-disc space-y-0.5 pl-4 text-xs">
              @for (d of detalles(); track d) { <li>{{ d }}</li> }
            </ul>
          }
        </div>
      }

      <div class="mt-5 flex justify-end gap-2">
        <button type="button" (click)="closed.emit()"
                class="rounded-lg px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100
                       dark:text-gray-300 dark:hover:bg-gray-700">
          Cancelar
        </button>
        <button type="button" (click)="guardar()" [disabled]="!puedeGuardar()"
                class="rounded-lg bg-cyan-600 px-4 py-2 text-sm font-semibold text-white hover:bg-cyan-700
                       disabled:cursor-not-allowed disabled:opacity-50">
          {{ guardando() ? 'Guardando…' : 'Cambiar contraseña' }}
        </button>
      </div>
    }
  </div>
</div>
  `
})
export class ChangePasswordDialogComponent {
  private readonly auth = inject(AuthService);

  readonly closed = output<void>();

  readonly max = PASSWORD_MAX;
  readonly inputClass =
    'w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-cyan-500 ' +
    'focus:outline-none dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100';

  actual = '';
  readonly nueva = signal('');
  readonly repetida = signal('');

  readonly verActual = signal(false);
  readonly verNueva = signal(false);
  readonly guardando = signal(false);
  readonly done = signal(false);
  readonly error = signal<string | null>(null);
  readonly detalles = signal<string[]>([]);

  private readonly actualInput = viewChild<ElementRef<HTMLInputElement>>('actualInput');

  private readonly email = this.auth.getUser()?.email ?? '';
  private backdropArmed = false;

  constructor() {
    // El foco entra al primer campo en cuanto el diálogo existe en el DOM.
    setTimeout(() => this.actualInput()?.nativeElement.focus());
  }

  readonly reglas = () => evaluatePassword(this.nueva(), this.email);
  readonly strength = () => passwordStrength(this.nueva());
  readonly strengthLabel = () => STRENGTH_LABEL[this.strength()];
  readonly coinciden = () => this.nueva() === this.repetida();

  barColor(): string {
    return ['bg-red-500', 'bg-red-500', 'bg-amber-500', 'bg-emerald-500', 'bg-emerald-600'][this.strength()]!;
  }

  textColor(): string {
    return this.strength() <= 1
      ? 'text-red-600 dark:text-red-400'
      : this.strength() === 2
        ? 'text-amber-600 dark:text-amber-400'
        : 'text-emerald-600 dark:text-emerald-400';
  }

  puedeGuardar(): boolean {
    return (
      !this.guardando() &&
      this.actual.length > 0 &&
      this.coinciden() &&
      passwordIsValid(this.nueva(), this.email)
    );
  }

  guardar(): void {
    if (!this.puedeGuardar()) return;

    this.guardando.set(true);
    this.error.set(null);
    this.detalles.set([]);

    this.auth.changePassword(this.actual, this.nueva()).subscribe({
      next: () => {
        this.guardando.set(false);
        this.done.set(true);
        // Nada de contraseñas en memoria una vez hecho el cambio.
        this.actual = '';
        this.nueva.set('');
        this.repetida.set('');
      },
      error: (err: HttpErrorResponse) => {
        this.guardando.set(false);
        this.error.set(err.error?.message ?? 'No se pudo cambiar la contraseña.');
        // La API devuelve en `details.reglas` las que no se cumplieron, por si
        // su política es más estricta que la copia que valida esta pantalla.
        const reglas = err.error?.details?.reglas;
        this.detalles.set(Array.isArray(reglas) ? reglas : []);
      }
    });
  }

  /** El fondo solo cierra si el gesto empezó y terminó en él. */
  armBackdrop(event: MouseEvent): void {
    this.backdropArmed = event.target === event.currentTarget;
  }

  closeOnBackdrop(event: MouseEvent): void {
    const cerrar = this.backdropArmed && event.target === event.currentTarget;
    this.backdropArmed = false;
    if (cerrar) this.closed.emit();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.closed.emit();
  }
}
